/**
 * DAG Object creation and hashing utilities
 */
import { sha256 } from '@noble/hashes/sha256';
import { bytesToHex } from '@noble/hashes/utils';
import type { DAGObject, DAGObjectWithoutHash, ContentTypes } from './types.js';

/**
 * Recursively sort object keys for deterministic serialization
 */
function sortKeys(obj: unknown): unknown {
  if (obj === null || typeof obj !== 'object') {
    return obj;
  }
  if (Array.isArray(obj)) {
    return obj.map(sortKeys);
  }
  const sorted: Record<string, unknown> = {};
  for (const key of Object.keys(obj).sort()) {
    sorted[key] = sortKeys((obj as Record<string, unknown>)[key]);
  }
  return sorted;
}

/**
 * Serialize object for hashing (deterministic JSON)
 */
function serializeForHash(obj: DAGObjectWithoutHash): string {
  return JSON.stringify(sortKeys(obj));
}

/**
 * Compute SHA-256 hash of object content
 */
export function computeHash(obj: DAGObjectWithoutHash): string {
  const serialized = serializeForHash(obj);
  const bytes = new TextEncoder().encode(serialized);
  const hashBytes = sha256(bytes);
  return bytesToHex(hashBytes);
}

/**
 * Create a DAG object with computed hash (unsigned)
 */
export function createDAGObject<T extends ContentTypes>(
  obj: DAGObjectWithoutHash<T>
): Omit<DAGObject<T>, 'signature'> {
  const hash = computeHash(obj);
  return {
    ...obj,
    hash,
  };
}

/**
 * Verify that an object's hash matches its content
 */
export function verifyHash(obj: DAGObject): boolean {
  const { hash, signature, ...rest } = obj;
  const computedHash = computeHash(rest as DAGObjectWithoutHash);
  return hash === computedHash;
}

/**
 * Get the data that should be signed (the hash)
 */
export function getSigningData(obj: Omit<DAGObject, 'signature'>): Uint8Array {
  return new TextEncoder().encode(obj.hash);
}
